/**
 * PlanningSection 補貨參數 (SR0.2) — read-only view of the DD!53–67 replenishment
 * planning block (J=SHOULD) with a chunk-Edit into EditReplenishmentModal.
 *
 * These are Tier-2 demand-driven purchasing inputs; the whole block is optional
 * and NEVER gates 產品就緒 (Fixed decision 1). Severity/flags render with tone +
 * icon + label per SemanticType (never colour-only); the numeric grid is
 * NEUTRAL — no colour-by-value. The dormant `*Auto` v2 fields are not shown.
 *
 * Self-contained: it hosts its own EditReplenishmentModal + open state so it can
 * be dropped into the detail-page Sider by the SR4.1 wiring pass with a single
 * `<PlanningSection product={product} onSaved={refresh} />` line.
 */

import { useState } from 'react';
import { Descriptions, Empty, Space, Tag, theme } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, StopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { renderDash } from '@/components/common/TableContainer';
import type { Product } from '@/types/api/product';
import SectionHeader from './SectionHeader';
import EditReplenishmentModal from '../EditReplenishmentModal';

interface PlanningSectionProps {
  product: Product;
  /** Refresh the detail view after a successful edit. */
  onSaved?: () => void;
}

export const PlanningSection = ({ product, onSaved }: PlanningSectionProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const [editOpen, setEditOpen] = useState(false);

  const planning = product.planning ?? {};
  const hasAny = Object.values(planning).some(
    (value) => value === true || (typeof value === 'number' && Number.isFinite(value))
  );

  const num = (value: number | null | undefined): React.ReactNode =>
    renderDash(value != null ? value : undefined);

  const percent = (value: number | null | undefined): React.ReactNode =>
    value != null ? `${value}%` : renderDash(undefined);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <SectionHeader
          title={t('products:planning.sectionTitle')}
          subtitle={t('products:planning.sectionSubtitle')}
          editLabel={t('common:actions.edit')}
          onEdit={() => setEditOpen(true)}
          editDisabled={!product.isActive}
        />

        {hasAny ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions column={{ xs: 1, md: 3 }} layout="vertical" bordered>
              <Descriptions.Item label={t('products:fields.planning.reorderPoint')}>
                {num(planning.reorderPoint)}
              </Descriptions.Item>
              <Descriptions.Item label={t('products:fields.planning.preferredStockLevel')}>
                {num(planning.preferredStockLevel)}
              </Descriptions.Item>
              <Descriptions.Item label={t('products:fields.planning.reorderMultiple')}>
                {num(planning.reorderMultiple)}
              </Descriptions.Item>
              <Descriptions.Item label={t('products:fields.planning.purchaseLeadTimeDays')}>
                {num(planning.purchaseLeadTimeDays)}
              </Descriptions.Item>
              <Descriptions.Item label={t('products:fields.planning.safetyStockDays')}>
                {num(planning.safetyStockDays)}
              </Descriptions.Item>
              <Descriptions.Item label={t('products:fields.planning.expectedDemandChangePct')}>
                {percent(planning.expectedDemandChangePct)}
              </Descriptions.Item>
            </Descriptions>

            {(planning.seasonalDemand || planning.doNotOrder || planning.preOrder) && (
              <Space size={[8, 8]} wrap>
                <span style={{ color: token.colorTextSecondary, fontSize: token.fontSizeSM }}>
                  {t('products:planning.groups.flags')}
                </span>
                {planning.seasonalDemand && (
                  <Tag color="blue" icon={<CalendarOutlined />}>
                    {t('products:fields.planning.seasonalDemand')}
                  </Tag>
                )}
                {planning.doNotOrder && (
                  <Tag color="error" icon={<StopOutlined />}>
                    {t('products:planning.doNotOrderTag')}
                  </Tag>
                )}
                {planning.preOrder && (
                  <Tag color="gold" icon={<ClockCircleOutlined />}>
                    {t('products:planning.preOrderTag')}
                  </Tag>
                )}
              </Space>
            )}
          </Space>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('products:planning.empty')}
          />
        )}
      </div>

      <EditReplenishmentModal
        open={editOpen}
        product={product}
        onCancel={() => setEditOpen(false)}
        onSaved={() => onSaved?.()}
      />
    </Space>
  );
};

export default PlanningSection;
